'use client'

import * as React from 'react'
import Script from 'next/script'

import { Skeleton } from '@/components/ui/tailwind/Skeleton'

const HUBSPOT_FORMS_SCRIPT_URL = 'https://js.hsforms.net/forms/v2.js'
const HUBSPOT_IFRAME_ID_PREFIX = 'hs-form-iframe-'

interface HubSpotFormProps {
  portalId: string
  formId: string
}

const disconnectHubSpotResizeObservers = (formTarget: HTMLElement) => {
  formTarget.querySelectorAll(`iframe[id^="${HUBSPOT_IFRAME_ID_PREFIX}"]`).forEach((iframe) => {
    const shellId = iframe.id.slice(HUBSPOT_IFRAME_ID_PREFIX.length)
    window.hbspt?.forms.shellObservers?.[shellId]?.disconnect()
  })
}

const followFormHeightOnNextFrame = (formTarget: HTMLElement, wrapper: HTMLElement) => {
  let pendingFrame = 0
  const copyFormHeight = () => {
    wrapper.style.height = `${formTarget.querySelector('iframe')?.offsetHeight ?? 0}px`
  }
  const formResizeObserver = new MutationObserver(() => {
    cancelAnimationFrame(pendingFrame)
    pendingFrame = requestAnimationFrame(copyFormHeight)
  })

  formResizeObserver.observe(formTarget, { subtree: true, childList: true, attributeFilter: ['height', 'style'] })

  return () => {
    formResizeObserver.disconnect()
    cancelAnimationFrame(pendingFrame)
  }
}

const useFormTargetId = () => `hubspot-form-${React.useId().replace(/[^\w-]/g, '')}`

export const HubSpotForm: React.FC<HubSpotFormProps> = ({ portalId, formId }) => {
  const formTargetId = useFormTargetId()
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const formTargetRef = React.useRef<HTMLDivElement>(null)
  const [isScriptReady, setIsScriptReady] = React.useState(false)
  const [isFormReady, setIsFormReady] = React.useState(false)

  React.useEffect(() => {
    const wrapper = wrapperRef.current
    const formTarget = formTargetRef.current

    if (!isScriptReady || !wrapper || !formTarget || !window.hbspt) {
      return
    }

    const stopFollowingFormHeight = followFormHeightOnNextFrame(formTarget, wrapper)

    window.hbspt.forms.create({
      portalId,
      formId,
      target: `#${formTargetId}`,
      onFormReady: () => setIsFormReady(true),
    })

    return () => {
      stopFollowingFormHeight()
      disconnectHubSpotResizeObservers(formTarget)
      formTarget.replaceChildren()
    }
  }, [isScriptReady, portalId, formId, formTargetId])

  return (
    <>
      <Script src={HUBSPOT_FORMS_SCRIPT_URL} onReady={() => setIsScriptReady(true)} />
      <div ref={wrapperRef} className="h-0" aria-busy={!isFormReady}>
        <div id={formTargetId} ref={formTargetRef} />
      </div>
      {!isFormReady && <Skeleton className="mt-10 h-96 w-full" />}
    </>
  )
}
